import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Button, 
  Card, 
  Typography, 
  message, 
  Space, 
  Tag, 
  Input, 
  Popconfirm,
  Empty,
  Divider
} from 'antd';
import { 
  SearchOutlined, 
  ShopOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  SendOutlined,
  UserOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  StopOutlined,
} from '@ant-design/icons';
import branchService, { Branch, BranchesListResponse } from '../services/branchService';
import branchSellerRelationshipService, { 
  BranchSellerRelationship, 
  BranchSellerRelationshipPopulated,
  BranchSellerRelationshipsListResponse,
  BranchSellerRelationshipData 
} from '../services/branchSellerRelationshipService';

const { Title } = Typography;

const Branches: React.FC = () => {
  // Branch search states
  const [searchBranches, setSearchBranches] = useState<Branch[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Relationship states
  const [relationships, setRelationships] = useState<BranchSellerRelationshipPopulated[]>([]);
  const [relationshipsLoading, setRelationshipsLoading] = useState(false);
  
  // Get current user info
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentSellerId = currentUser.id;
  
  // Determine user role
  const isSeller = currentUser.is_main_seller === true;
  const isBranchManager = currentUser.branch_id && !currentUser.is_main_seller;

  // Search for branches
  const searchBranchesForConnection = useCallback(async () => {
    if (!searchTerm.trim()) {
      setSearchBranches([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const params = {
        search: searchTerm,
        page: 1,
        pageSize: 50 // Get more results for search
      };

      const response: BranchesListResponse = await branchService.getBranches(params);
      
      const branches = response.data.map(item => ({
        ...item.attributes,
        id: item.id
      }));
      
      setSearchBranches(branches);
    } catch (error: any) {
      console.error('Error searching branches:', error);
      message.error('Failed to search branches');
    } finally {
      setSearchLoading(false);
    }
  }, [searchTerm]);

  // Fetch seller's relationships
  const fetchRelationships = useCallback(async () => {
    setRelationshipsLoading(true);
    try {
      const response: BranchSellerRelationshipsListResponse = await branchSellerRelationshipService.getRelationshipsBySeller(currentSellerId);
      
      setRelationships(response.data.map(item => ({
        ...item.attributes,
        id: item.id
      })));
    } catch (error: any) {
      console.error('Error fetching relationships:', error);
      message.error('Failed to fetch relationships');
    } finally {
      setRelationshipsLoading(false);
    }
  }, [currentSellerId]);

  // Always fetch relationships on component mount
  useEffect(() => {
    fetchRelationships();
  }, [currentSellerId, fetchRelationships]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchBranchesForConnection();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, searchBranchesForConnection]);

  // Send connection request to a branch
  const handleSendConnectionRequest = async (branchId: number, branchName: string) => {
    try {
      const relationshipData: BranchSellerRelationshipData = {
        branch_id: branchId,
        seller_id: currentSellerId,
        relationship_status: 'inactive' // Start as inactive, branch can activate it
      };

      await branchSellerRelationshipService.createRelationship(relationshipData);
      message.success(`Connection request sent to ${branchName}. Waiting for branch approval.`);
      
      // Refresh relationships
      fetchRelationships();
    } catch (error: any) {
      console.error('Error sending connection request:', error);
      if (error.response?.data?.message?.includes('already exists')) {
        message.error('Connection request already exists for this branch');
      } else {
        message.error('Failed to send connection request');
      }
    }
  };

  // Cancel/Withdraw connection request
  const handleCancelRequest = async (relationshipId: number) => {
    try {
      await branchSellerRelationshipService.deleteRelationship(relationshipId);
      message.success('Connection request cancelled');
      fetchRelationships();
    } catch (error: any) {
      console.error('Error cancelling request:', error);
      message.error('Failed to cancel request');
    }
  };

  // Withdraw from active connection
  const handleWithdrawConnection = async (relationshipId: number, branchName: string) => {
    try {
      await branchSellerRelationshipService.deleteRelationship(relationshipId);
      message.success(`Connection withdrawn from ${branchName}`);
      fetchRelationships();
    } catch (error: any) {
      console.error('Error withdrawing connection:', error);
      message.error('Failed to withdraw connection');
    }
  };

  // Accept connection request from branch
  const handleAcceptRequest = async (relationshipId: number, branchName: string) => {
    try {
      await branchSellerRelationshipService.updateRelationship(relationshipId, {
        relationship_status: 'active'
      });
      message.success(`Connection request accepted from ${branchName}`);
      fetchRelationships();
    } catch (error: any) {
      console.error('Error accepting request:', error);
      message.error('Failed to accept connection request');
    }
  };

  // Reject connection request from branch
  const handleRejectRequest = async (relationshipId: number, branchName: string) => {
    try {
      await branchSellerRelationshipService.deleteRelationship(relationshipId);
      message.success(`Connection request rejected from ${branchName}`);
      fetchRelationships();
    } catch (error: any) {
      console.error('Error rejecting request:', error);
      message.error('Failed to reject connection request');
    }
  };

  // Helper functions to organize relationship data
  const getPendingRequests = () => {
    return relationships.filter(rel => rel.relationship_status === 'inactive');
  };

  const getConnectedBranches = () => {
    return relationships.filter(rel => rel.relationship_status === 'active');
  };

  const getSuspendedBranches = () => {
    return relationships.filter(rel => rel.relationship_status === 'suspended');
  };

  // Columns for branch search results
  const searchColumns = [
    {
      title: 'Branch Name',
      dataIndex: 'branch_name',
      key: 'branch_name',
      render: (text: string, record: Branch) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <Tag color={record.branch_type === 'retail' ? 'blue' : 
                       record.branch_type === 'wholesale' ? 'green' : 
                       record.branch_type === 'warehouse' ? 'orange' : 'purple'}>
              {record.branch_type}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (record: Branch) => (
        <div>
          <div style={{ marginBottom: 4 }}>
            <PhoneOutlined style={{ marginRight: 4 }} />
            {record.phone}
          </div>
          <div>
            <MailOutlined style={{ marginRight: 4 }} />
            {record.email}
          </div>
        </div>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      render: (text: string) => (
        <div style={{ maxWidth: 200 }}>
          <EnvironmentOutlined style={{ marginRight: 4 }} />
          {text}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (record: Branch) => {
        // Check if already connected
        const existingRelationship = relationships.find(rel => rel.branch_id === record.id);
        
        if (existingRelationship) {
          const statusColors = {
            active: 'green',
            inactive: 'orange', // Shows as pending/waiting for approval
            suspended: 'red'
          };
          const statusLabels = {
            active: 'CONNECTED',
            inactive: 'PENDING APPROVAL',
            suspended: 'SUSPENDED'
          };
          return (
            <Space>
              <Tag color={statusColors[existingRelationship.relationship_status]}>
                {statusLabels[existingRelationship.relationship_status]}
              </Tag>
              {existingRelationship.relationship_status === 'inactive' && (
                <Space>
                  {isBranchManager && (
                    <>
                      <Button 
                        type="primary" 
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={() => handleAcceptRequest(existingRelationship.id, record.branch_name)}
                      >
                        Accept
                      </Button>
                      <Popconfirm
                        title={`Are you sure you want to reject the connection request from ${record.branch_name}?`}
                        onConfirm={() => handleRejectRequest(existingRelationship.id, record.branch_name)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button 
                          danger 
                          size="small"
                          icon={<StopOutlined />}
                        >
                          Reject
                        </Button>
                      </Popconfirm>
                    </>
                  )}
                  {isSeller && (
                    <Button 
                      type="link" 
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => handleCancelRequest(existingRelationship.id)}
                    >
                      Cancel Request
                    </Button>
                  )}
                </Space>
              )}
              {existingRelationship.relationship_status === 'active' && (
                <Popconfirm
                  title={`Are you sure you want to withdraw from ${record.branch_name}?`}
                  onConfirm={() => handleWithdrawConnection(existingRelationship.id, record.branch_name)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button 
                    type="link" 
                    danger 
                    size="small"
                    icon={<CloseOutlined />}
                  >
                    Withdraw
                  </Button>
                </Popconfirm>
              )}
            </Space>
          );
        }
        
        return (
          <Button 
            type="primary" 
            size="small"
            icon={<SendOutlined />}
            onClick={() => handleSendConnectionRequest(record.id, record.branch_name)}
          >
            Send Request
          </Button>
        );
      },
    },
  ];

  // Columns for relationship tables (pending requests and connected branches)
  const relationshipColumns = [
    {
      title: 'Branch Name',
      dataIndex: ['branch', 'data', 'attributes', 'branch_name'],
      key: 'branch_name',
      render: (text: string, record: any) => {
        const branchData = record.branch?.data?.attributes;
        return branchData ? (
          <div>
            <div style={{ fontWeight: 'bold' }}>{branchData.branch_name}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              <Tag color={branchData.branch_type === 'retail' ? 'blue' : 
                         branchData.branch_type === 'wholesale' ? 'green' : 
                         branchData.branch_type === 'warehouse' ? 'orange' : 'purple'}>
                {branchData.branch_type}
              </Tag>
            </div>
          </div>
        ) : <span>Branch #{record.branch_id}</span>;
      },
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (record: any) => {
        const branchData = record.branch?.data?.attributes;
        return branchData ? (
          <div>
            <div style={{ marginBottom: 4 }}>
              <PhoneOutlined style={{ marginRight: 4 }} />
              {branchData.phone}
            </div>
            <div>
              <MailOutlined style={{ marginRight: 4 }} />
              {branchData.email}
            </div>
          </div>
        ) : '-';
      },
    },
    {
      title: 'Status',
      dataIndex: 'relationship_status',
      key: 'relationship_status',
      render: (status: string) => {
        const statusColors = {
          active: 'green',
          inactive: 'orange',
          suspended: 'red'
        };
        const statusLabels = {
          active: 'CONNECTED',
          inactive: 'PENDING APPROVAL',
          suspended: 'SUSPENDED'
        };
        return (
          <Tag color={statusColors[status as keyof typeof statusColors]}>
            {statusLabels[status as keyof typeof statusLabels]}
          </Tag>
        );
      },
    },
    {
      title: 'Total Orders',
      dataIndex: 'total_order_count',
      key: 'total_order_count',
      render: (count: number) => count || 0,
    },
    {
      title: 'Total Value',
      dataIndex: 'total_order_value',
      key: 'total_order_value',
      render: (value: number) => `$${(value || 0).toFixed(2)}`,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: BranchSellerRelationshipPopulated) => {
        const branchData = record.branch?.data?.attributes;
        const branchName = branchData?.branch_name || `Branch #${record.branch_id}`;
        
        return (
          <Space>
            {record.relationship_status === 'inactive' && (
              <>
                {isBranchManager && (
                  <>
                    <Button 
                      type="primary" 
                      size="small"
                      icon={<CheckOutlined />}
                      onClick={() => handleAcceptRequest(record.id, branchName)}
                    >
                      Accept
                    </Button>
                    <Popconfirm
                      title={`Are you sure you want to reject the connection request from ${branchName}?`}
                      onConfirm={() => handleRejectRequest(record.id, branchName)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button 
                        danger 
                        size="small"
                        icon={<StopOutlined />}
                      >
                        Reject
                      </Button>
                    </Popconfirm>
                  </>
                )}
                {isSeller && (
                  <Button 
                    type="link" 
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => handleCancelRequest(record.id)}
                  >
                    Cancel Request
                  </Button>
                )}
              </>
            )}
            {record.relationship_status === 'active' && (
              <Popconfirm
                title={`Are you sure you want to withdraw from ${branchName}?`}
                onConfirm={() => handleWithdrawConnection(record.id, branchName)}
                okText="Yes"
                cancelText="No"
              >
                <Button 
                  type="link" 
                  danger 
                  size="small"
                  icon={<CloseOutlined />}
                >
                  Withdraw
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Title level={2} style={{ margin: 0 }}>
            <ShopOutlined style={{ marginRight: 8 }} />
            Branch Connections
          </Title>
        </div>

        {/* Search Section */}
        <div style={{ marginBottom: 24 }}>
          <Input
            placeholder="Search for branches to connect with..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            size="large"
            style={{ maxWidth: 400 }}
          />
        </div>

        {/* Pending Requests Section */}
        {getPendingRequests().length > 0 && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Title level={4} style={{ margin: 0, color: '#fa8c16' }}>
                <CheckCircleOutlined style={{ marginRight: 8 }} />
                Pending Requests ({getPendingRequests().length})
              </Title>
              <p style={{ color: '#666', marginTop: '4px', marginBottom: 0 }}>
                Connection requests from branches - accept or reject them
              </p>
            </div>
            <Table
              columns={relationshipColumns}
              dataSource={getPendingRequests()}
              loading={relationshipsLoading}
              rowKey="id"
              pagination={false}
              size="small"
              style={{ marginBottom: 24 }}
            />
            <Divider />
          </>
        )}

        {/* Connected Branches Section */}
        {getConnectedBranches().length > 0 && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Title level={4} style={{ margin: 0, color: '#52c41a' }}>
                <UserOutlined style={{ marginRight: 8 }} />
                Connected Branches ({getConnectedBranches().length})
              </Title>
              <p style={{ color: '#666', marginTop: '4px', marginBottom: 0 }}>
                Active connections where you can place orders
              </p>
            </div>
            <Table
              columns={relationshipColumns}
              dataSource={getConnectedBranches()}
              loading={relationshipsLoading}
              rowKey="id"
              pagination={false}
              size="small"
              style={{ marginBottom: 24 }}
            />
            <Divider />
          </>
        )}

        {/* Suspended Branches Section */}
        {getSuspendedBranches().length > 0 && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Title level={4} style={{ margin: 0, color: '#ff4d4f' }}>
                <CloseOutlined style={{ marginRight: 8 }} />
                Suspended Connections ({getSuspendedBranches().length})
              </Title>
              <p style={{ color: '#666', marginTop: '4px', marginBottom: 0 }}>
                Connections that have been suspended by the branch
              </p>
            </div>
            <Table
              columns={relationshipColumns}
              dataSource={getSuspendedBranches()}
              loading={relationshipsLoading}
              rowKey="id"
              pagination={false}
              size="small"
              style={{ marginBottom: 24 }}
            />
            <Divider />
          </>
        )}

        {/* Search Results Section */}
        <div style={{ marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            <SearchOutlined style={{ marginRight: 8 }} />
            Available Branches
          </Title>
          <p style={{ color: '#666', marginTop: '4px', marginBottom: 0 }}>
            Search and send connection requests to new branches
          </p>
        </div>
        
        {searchBranches.length > 0 ? (
          <Table
            columns={searchColumns}
            dataSource={searchBranches}
            loading={searchLoading}
            rowKey="id"
            pagination={false}
            size="small"
          />
        ) : searchTerm ? (
          <Empty 
            description="No branches found matching your search"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Empty 
            description="Enter a branch name to search for connections"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>
    </div>
  );
};

export default Branches;